import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { MaterialModel, JsonMaterialModel } from '../../models/low-level/material.model';
import {
    JsonLocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModelFromJson,
} from '../../models/low-level/local-storage-notification-material-order.model';
import { LZStringService } from '../l-z-string/l-z-string.service';
import { catchError, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

const MATERIAL_KEY = 'MATERIAL-material';
const MATERIAL_ORDER_KEY = 'MATERIAL-order';

@Injectable({ providedIn: 'root' })
export class StorageMaterialConcurrentService {
    public constructor(
        private readonly concurrentService: ConcurrentUsageService,
        private readonly LZService: LZStringService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public getAllMaterialCreateOrders(): Observable<{
        material: LocalStorageNotificationMaterialOrderModel[];
        timestamp: number;
    }> {
        return this.concurrentService.readDataForKey(MATERIAL_ORDER_KEY).pipe(
            map((data) => {
                if (data?.length > 0) {
                    const rawJsonList = JSON.parse(data[0].value) as JsonLocalStorageNotificationMaterialOrderModel[];
                    return {
                        material: rawJsonList.map(
                            (rawEntry) => new LocalStorageNotificationMaterialOrderModelFromJson(rawEntry),
                        ),
                        timestamp: data[0].timestamp,
                    };
                }
                throw new Error('[Concurrency Store Material] Order Materials: no order materials found in store.');
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Material] Order Materials: Couldn't load value. Error: ${error}`);
                return of({ material: [], timestamp: 0 });
            }),
        );
    }

    public saveMaterialCreateOrders(
        notificationMaterialOrders: LocalStorageNotificationMaterialOrderModel[],
        timestamp?: number,
    ): Observable<void> {
        const outData = JSON.stringify(notificationMaterialOrders);
        return this.concurrentService.writeDataForKey(MATERIAL_ORDER_KEY, outData, timestamp).pipe(
            map(() => undefined),
            catchError((error) => {
                this.log.warn(
                    `[Concurrency Store Material] Order Materials: Couldn't store ordered material value. Error: ${error}`,
                );
                return of(undefined);
            }),
        );
    }

    public saveMaterials(materials: MaterialModel[], timestamp?: number): Observable<void> {
        const outData = JSON.stringify(materials);
        return this.concurrentService.writeDataForKey(MATERIAL_KEY, outData, timestamp).pipe(
            map(() => undefined),
            catchError((error) => {
                this.log.warn(
                    `[Concurrency Store Material] Order Materials: Couldn't store material value. Error: ${error}`,
                );
                return of(undefined);
            }),
        );
    }

    public getMaterials(): Observable<{ material: JsonMaterialModel[]; timestamp: number }> {
        return this.concurrentService.readDataForKey(MATERIAL_KEY).pipe(
            map((data) => {
                if (data?.length > 0) {
                    return {
                        material: JSON.parse(data[0].value) as JsonMaterialModel[],
                        timestamp: data[0].timestamp,
                    };
                }
                throw new Error('[Concurrency Store Material] Order Materials: no order materials found in store.');
            }),
            catchError((error) => {
                this.log.warn(`[Concurrency Store Material] Order Materials: Couldn't load value. Error: ${error}`);
                return of({ material: [], timestamp: 0 });
            }),
        );
    }
}
